<template>
  <div class="band-rail">
    <div class="band-rail__stats">
      <span class="band-stat" title="Roster median damage against one body, per turn">
        <span class="band-stat__value">{{ round(band.median_single_target_dpt) }}</span>
        <span class="band-stat__label">med dmg / turn</span>
      </span>
      <span class="band-stat" title="Roster median survival against a peer of its own rate">
        <span class="band-stat__value">{{ round(band.median_turns_to_fall) }}</span>
        <span class="band-stat__label">med turns to fall</span>
      </span>
      <span class="band-stat" title="Roster median hit points at this grade">
        <span class="band-stat__value">{{ round(band.median_hp) }}</span>
        <span class="band-stat__label">med hp</span>
      </span>
      <span class="band-stat" title="Skills whose numbers disagree with the tuning curve">
        <span class="band-stat__value" :class="{ 'is-warn': band.off_curve_skills > 0 }">
          {{ band.off_curve_skills }}
        </span>
        <span class="band-stat__label">off curve</span>
      </span>
    </div>

    <div class="band-rail__roster">
      <span
        v-for="(count, metatype) in band.metatype_counts"
        :key="metatype"
        class="roster-count"
        :class="{ 'is-empty': count === 0 }"
        :title="`${count} ${metatype}`"
      >
        <MetatypeBadge size="sm" :metatype="metatype" />
        <span class="roster-count__n">{{ count }}</span>
      </span>
    </div>

    <div class="band-rail__actions">
      <button
        v-if="band.findings?.length"
        type="button"
        class="rail-btn"
        :class="{ 'is-on': openPanel === 'findings' }"
        @click="$emit('open', 'findings')"
      >
        {{ band.findings.length }} finding{{ band.findings.length === 1 ? '' : 's' }}
      </button>
      <button
        type="button"
        class="rail-btn"
        :class="{ 'is-on': openPanel === 'model' }"
        @click="$emit('open', 'model')"
      >
        How it is measured
      </button>
    </div>
  </div>
</template>

<script setup>
import MetatypeBadge from './MetatypeBadge.vue';

defineProps({
  band: { type: Object, required: true },
  openPanel: { type: String, default: null },
});

defineEmits(['open']);

function round(value) {
  if (value === null || value === undefined) return '—';
  return Math.round(value * 10) / 10;
}
</script>

<style scoped>
/* The band used to be a panel three rows tall carrying five oversized numbers.
   It is context, not the subject: at rail height it still answers "compared
   with what?" while giving its vertical space back to the builds. */
.band-rail {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-lg);
  padding: 0.3rem var(--gm-gap-lg);
  background: var(--gm-surface-sunken);
  border-bottom: 1px solid var(--gm-border-dim);
}

.band-rail__stats {
  display: flex;
  align-items: baseline;
  gap: var(--gm-gap-lg);
}

.band-stat {
  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
  white-space: nowrap;
}

.band-stat__value {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--gm-text);
  font-variant-numeric: tabular-nums;
}

.band-stat__value.is-warn {
  color: var(--gm-accent-hi);
}

.band-stat__label {
  font-size: 0.58rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--gm-text-dim);
}

.band-rail__roster {
  display: flex;
  align-items: center;
  gap: var(--gm-gap);
  padding-left: var(--gm-gap-lg);
  border-left: 1px solid var(--gm-border-dim);
}

.roster-count {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.roster-count__n {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--gm-text);
  font-variant-numeric: tabular-nums;
}

.roster-count.is-empty {
  opacity: 0.5;
}

.roster-count.is-empty .roster-count__n {
  color: var(--gm-danger);
}

.band-rail__actions {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-xs);
  margin-left: auto;
}

.rail-btn {
  padding: 0.2rem 0.5rem;
  font-family: var(--gm-font-display);
  font-size: 0.56rem;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--gm-text-muted);
  background: transparent;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.rail-btn:hover {
  color: var(--gm-text);
  border-color: var(--gm-border);
}

.rail-btn.is-on {
  color: var(--gm-surface-0);
  background: var(--gm-accent);
  border-color: var(--gm-accent);
}

@media (max-width: 1200px) {
  .band-rail__roster {
    display: none;
  }
}
</style>
